import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, TextField, Chip, IconButton, Typography, Stack } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useQuery } from '@apollo/client';
import { getQuickChips } from './../Constants';
import { AI_CONFIG_QUERY } from '../../graphql';
import { isDemo } from 'op-utils/helpers';
import ChatDisclaimer from '../ChatDisclaimer';
import { theme } from 'theme';
import { Grow } from '@mui/material';
import { logDatadogRumCustomAction } from 'op-utils/datadogRumCustomAction';

const getRandomChips = (chips: string[]) => {
  const shuffled = [...chips].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, 3);
};

export const useAiAskBarState = (onInputChange: (messageText: string) => void) => {
  const { data: aiConfigData } = useQuery(AI_CONFIG_QUERY);
  const hasDocumentGenerationFeature = aiConfigData?.aiConfig?.hasDocumentGenerationFeature ?? false;
  const quickChips = useMemo(() => getQuickChips(hasDocumentGenerationFeature), [hasDocumentGenerationFeature]);

  const textfieldRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState(() => getRandomChips(quickChips));
  const placeholder = isDemo
    ? 'Summarise history, create imaging requests, or draft a consult note...'
    : "Start typing your question – I'll pull info from the patient record.";

  // Update suggestions when quickChips changes (e.g., when feature flag loads)
  useEffect(() => {
    setSuggestions(getRandomChips(quickChips));
  }, [quickChips]);

  const refreshSuggestions = useCallback(() => {
    setSuggestions(getRandomChips(quickChips));
  }, [quickChips]);

  const submitInput = useCallback(() => {
    const messageText = value.trim();
    if (messageText) {
      onInputChange(messageText);
      setValue('');
    }
  }, [value, onInputChange]);

  return {
    textfieldRef,
    value,
    setValue,
    suggestions,
    placeholder,
    refreshSuggestions,
    submitInput,
  };
};

export const AiAskInput: React.FC<{
  textfieldRef: React.RefObject<HTMLInputElement>;
  value: string;
  onChangeValue: (next: string) => void;
  onSubmit: () => void;
  placeholder: string;
  isSubmitDisabled?: boolean;
}> = ({ textfieldRef, value, onChangeValue, onSubmit, placeholder, isSubmitDisabled = false }) => {
  const handleSubmit = useCallback(() => {
    if (isSubmitDisabled) return;
    onSubmit();
  }, [isSubmitDisabled, onSubmit]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #E0E0E0',
        borderRadius: '16px',
        background: '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        padding: '0px 4px 12px 12px',
        position: 'relative',
        flexDirection: 'column',
        gap: 1,
      }}
      data-testid="ai-ask-bar-container">
      <TextField
        fullWidth
        inputRef={textfieldRef}
        variant="standard"
        placeholder={placeholder}
        InputProps={{
          disableUnderline: true,
          sx: {
            minHeight: 68,
            fontSize: 16,
            background: 'transparent',
            padding: '16px 0px 0px 0px !important',
            alignItems: 'start',
          },
        }}
        inputProps={{
          'aria-label': 'Ask using AI',
          'data-testid': 'ai-ask-bar-input',
          style: { padding: 0 },
        }}
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            handleSubmit();
          }
        }}
        multiline
        maxRows={8}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          width: '100%',
          gap: 2,
          paddingRight: 2,
        }}>
        <Typography
          variant="body2"
          sx={{ color: isSubmitDisabled ? 'text.disabled' : 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}
          data-testid="ai-ask-bar-label">
          Ask using AI
        </Typography>
        <IconButton
          tabIndex={0}
          aria-label="Submit AI question"
          data-testid="ai-ask-bar-submit"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{
            borderRadius: '50%',
            width: 28,
            height: 28,
            '&.MuiButtonBase-root': { backgroundColor: 'primary.main' },
            '&.Mui-disabled': { backgroundColor: 'grey.200' },
          }}>
          <ArrowUpwardIcon sx={{ width: 20, height: 20, color: 'primary.contrastText' }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export const AiAskSuggestions: React.FC<{
  suggestions: string[];
  onRefresh: () => void;
  onSelect: (label: string) => void;
  hide?: boolean;
  withTopPadding?: boolean;
  /** For Datadog RUM custom actions */
  patientId?: string;
  askBarLayout?: 'default' | 'popover';
  conversationId?: number | null;
}> = ({
  suggestions,
  onRefresh,
  onSelect,
  hide = false,
  withTopPadding = true,
  patientId,
  askBarLayout = 'default',
  conversationId,
}) => {
  const [refreshTick, setRefreshTick] = React.useState(0);
  if (hide) return null;

  const handleRefresh = () => {
    logDatadogRumCustomAction('ai_chat_inspiration_refresh', {
      patientId,
      viewMode: askBarLayout as 'default' | 'popover',
      conversationId: conversationId ?? null,
    });
    setRefreshTick((t) => t + 1);
    onRefresh();
  };

  return (
    <Box sx={{ paddingTop: withTopPadding ? '16px' : 0 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          Need inspiration?
        </Typography>
        <IconButton
          sx={{ height: 24, width: 24, p: 0 }}
          tabIndex={0}
          aria-label="Refresh suggestions"
          data-testid="ai-ask-bar-refresh"
          onClick={handleRefresh}>
          <SyncIcon sx={{ width: 20, height: 20, color: 'action.active' }} />
        </IconButton>
      </Stack>
      <Box gap={1} display="flex" flexWrap="wrap">
        {suggestions.map((label, idx) => (
          <Grow
            key={`${label}-${refreshTick}`}
            in
            timeout={200 + idx * 200}
            style={{
              transformOrigin: '0 0 0',
            }}>
            <Chip
              key={label}
              label={label}
              tabIndex={0}
              clickable
              aria-label={label}
              data-testid={`ai-ask-bar-suggestion-${idx}`}
              sx={{
                p: '6px',
                maxWidth: '100%',
                height: 'auto',
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              onClick={() => {
                logDatadogRumCustomAction('ai_chat_inspiration_select', {
                  patientId,
                  viewMode: askBarLayout as 'default' | 'popover',
                  conversationId: conversationId ?? null,
                  value: label,
                });
                onSelect(label);
              }}
            />
          </Grow>
        ))}
      </Box>
    </Box>
  );
};

export const AiAskBar: React.FC<{
  onInputChange: (messageText: string) => void;
  hideSuggestions?: boolean;
  layout?: 'default' | 'popover';
  isSubmitDisabled?: boolean;
  autoFocus?: boolean;
  focusSignal?: number;
  patientId?: string;
  conversationId?: number | null;
}> = ({
  onInputChange,
  hideSuggestions = false,
  layout = 'default',
  isSubmitDisabled = false,
  autoFocus = false,
  focusSignal,
  patientId,
  conversationId,
}) => {
  const { textfieldRef, value, setValue, suggestions, placeholder, refreshSuggestions, submitInput } =
    useAiAskBarState(onInputChange);

  const isPopoverLayout = layout === 'popover';
  const showSuggestions = !hideSuggestions;

  useEffect(() => {
    if (!autoFocus) return;
    const timeoutId = window.setTimeout(() => {
      textfieldRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [autoFocus, textfieldRef]);

  useEffect(() => {
    if (focusSignal === undefined) return;
    const timeoutId = window.setTimeout(() => {
      textfieldRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timeoutId);
  }, [focusSignal, textfieldRef]);

  return (
    <Stack sx={{ width: '100%', maxWidth: 786 }}>
      {isPopoverLayout && showSuggestions && (
        <Box sx={{ pb: theme.spacing(4) }}>
          <AiAskSuggestions
            suggestions={suggestions}
            onRefresh={refreshSuggestions}
            onSelect={setValue}
            withTopPadding={false}
            patientId={patientId}
            askBarLayout={layout}
            conversationId={conversationId}
          />
        </Box>
      )}
      <AiAskInput
        textfieldRef={textfieldRef}
        value={value}
        onChangeValue={setValue}
        onSubmit={submitInput}
        placeholder={placeholder}
        isSubmitDisabled={isSubmitDisabled}
      />
      {!isPopoverLayout && showSuggestions && (
        <AiAskSuggestions
          suggestions={suggestions}
          onRefresh={refreshSuggestions}
          onSelect={setValue}
          withTopPadding
          patientId={patientId}
          askBarLayout={layout}
          conversationId={conversationId}
        />
      )}
      {isPopoverLayout && (
        <Box sx={{ py: theme.spacing(0.5) }}>
          <ChatDisclaimer />
        </Box>
      )}
    </Stack>
  );
};

export default AiAskBar;
